import {
  ChangeDetectionStrategy,
  Component,
  input,
  output,
  Signal,
  ViewChild,
} from '@angular/core';
import {
  takeUntilDestroyed,
  toObservable,
  toSignal,
} from '@angular/core/rxjs-interop';
import { ReactiveFormsModule } from '@angular/forms';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { InformationBoxComponent } from '@principle-theorem/ng-principle-shared';
import {
  IsoTimeSelectorComponent,
  NgCommonModule,
  TypedFormControl,
  TypedFormGroup,
  validFormGroupChanges$,
} from '@principle-theorem/ng-shared';
import { RecurrenceFormComponent } from '@principle-theorem/ng-tasks';
import { MarketingCampaign } from '@principle-theorem/principle-core';
import {
  ICustomReport,
  IMarketingCampaign,
} from '@principle-theorem/principle-core/interfaces';
import {
  IRecurrencePattern,
  Time24hrType,
  WithRef,
} from '@principle-theorem/shared';
import { map, switchMap, take } from 'rxjs/operators';
import { CampaignReportSummaryComponent } from '../campaign-report-summary/campaign-report-summary.component';
import { FileUploadSummaryComponent } from '../file-upload-summary/file-upload-summary.component';

export interface ICampaignEnrolmentFormData {
  isRecurring: boolean;
  maximumEntriesPerRun?: number;
  recurrencePattern?: IRecurrencePattern;
  recurrenceRunTime?: Time24hrType;
}

@Component({
  selector: 'pr-campaign-enrolment-settings',
  templateUrl: './campaign-enrolment-settings.component.html',
  styleUrl: './campaign-enrolment-settings.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgCommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatSelectModule,
    MatCheckboxModule,
    CampaignReportSummaryComponent,
    FileUploadSummaryComponent,
    InformationBoxComponent,
    IsoTimeSelectorComponent,
    RecurrenceFormComponent,
  ],
})
export class CampaignEnrolmentSettingsComponent {
  campaign = input.required<WithRef<IMarketingCampaign>>();
  customReport: Signal<WithRef<ICustomReport> | undefined>;
  maxEntriesPerRun = [100, 250, 500, 1000, 2500, 5000, 10000];
  form = new TypedFormGroup<ICampaignEnrolmentFormData>({
    isRecurring: new TypedFormControl(false),
    maximumEntriesPerRun: new TypedFormControl(undefined),
    recurrencePattern: new TypedFormControl(undefined),
    recurrenceRunTime: new TypedFormControl(undefined),
  });

  formChange = output<ICampaignEnrolmentFormData>();

  @ViewChild(RecurrenceFormComponent, { static: false })
  set recurrenceForm(recurrenceForm: RecurrenceFormComponent) {
    if (!recurrenceForm) {
      this.updateRecurrenceSettings(undefined);
    }

    if (recurrenceForm && !this.form.controls.recurrencePattern.value) {
      this.updateRecurrenceSettings(
        recurrenceForm.recurrenceFormGroup.generateRecurrencePattern()
      );
    }
  }

  constructor() {
    const campaign$ = toObservable(this.campaign);
    const customReport$ = campaign$.pipe(
      switchMap((campaign) => MarketingCampaign.customReport(campaign)),
      map((customReport) => (!customReport?.deleted ? customReport : undefined))
    );
    this.customReport = toSignal(customReport$);

    campaign$
      .pipe(take(1), takeUntilDestroyed())
      .subscribe((campaign) => this._setInitialValues(campaign));

    const formValue$ = validFormGroupChanges$(this.form);
    formValue$
      .pipe(takeUntilDestroyed())
      .subscribe((value) => this.formChange.emit(value));
  }

  updateRecurrenceSettings(pattern?: IRecurrencePattern): void {
    if (pattern) {
      this.form.controls.recurrencePattern.setValue(pattern);
      return;
    }

    this.form.patchValue({
      recurrencePattern: undefined,
      recurrenceRunTime: undefined,
    });
  }

  private _setInitialValues(campaign: WithRef<IMarketingCampaign>): void {
    this.form.patchValue(
      {
        ...campaign,
        isRecurring: !!campaign.recurrencePattern,
      },
      { emitEvent: false }
    );
  }
}
